// Convert kebab-case to camelCase: my-page-asset > myPageAsset
export const camelize = s => s.replace(/-(.)/g, l => l[1].toUpperCase());

// 🔴 THE ONE YOUTUBE VIDEO PATTERN. `extractYouTubeID` and the link box's shown
// address (`AdditionalVideosField.js`'s `displayVideoAddress`) both read it
// through `matchYouTubeVideo`; there is no second parser.
//
// Groups: 1 a youtube.com host (any subdomain, such as `m.` or `www.`), 2 the
// path between that host and the id, 3 `youtu.be`, 4 the id.
//
// 🔴 Ticket 24 step-8 fix (fourth pass, Ryan 2026-10-16) — two boundaries:
//   - AFTER the id: a refusal, not an allow-list. The 11th character must not
//     be followed by a letter, digit, `_` or `-`, so `youtu.be/SERVER_MOVED_3E`
//     is refused instead of saved as video `SERVER_MOVE` (staging, 2026-10-16),
//     while anything else may follow: the end of the link, `? & # /`, a space,
//     a full stop, a bracket.
//   - BEFORE the host: the start of the value or a character that cannot be
//     part of a host name, so a lookalike such as `evilyoutube.com` is not
//     YouTube. A `.` is allowed, which keeps real subdomains (`m.youtube.com`).
// No lookbehind: older iOS Safari cannot parse one, and a regex literal it
// cannot parse breaks the whole bundle.
//
// 🔴 Ticket 24 fifth pass (review 2, M-C) — the boundary before the host makes
// this pattern quadratic in the length of the value: from each starting place
// it walks every `label.` pair before failing (50,000 characters took over a
// second).
// Buyers' pages run it on every render, server rendering included, so the
// value is length-checked BEFORE it is matched (`MAX_YOUTUBE_LINK_LENGTH`).
const YOUTUBE_VIDEO_PATTERN = /(?:^|[^a-z0-9_-])(?:((?:[a-z0-9-]+\.)*youtube\.com)\/([^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?|shorts|live)\/|\S*?[?&]v=)|(youtu\.be)\/)([a-zA-Z0-9_-]{11})(?![a-zA-Z0-9_-])/i;

/**
 * 🔴 Ticket 24 fifth pass (review 2, M-C) — the longest value either YouTube
 * pattern will read: the classic longest address a browser accepts. A YouTube
 * link, even with its tracking settings or a few words in front of it, is a
 * small fraction of that. Anything longer names no video, so the patterns
 * never run on it: their cost is bounded by this, not by the value.
 */
export const MAX_YOUTUBE_LINK_LENGTH = 2048;

/**
 * The YouTube video a value names, read by the one pattern above.
 *
 * @param {*} url any stored or typed value
 * @returns {?{id: string, host: string, path: string}} `host` is as written
 *   (`www.youtube.com`, `m.youtube.com`, `youtu.be`); `path` is what sits
 *   between the host and the id (`watch?v=`, `shorts/`, `''` for youtu.be).
 *   Null when the value names no video.
 */
export const matchYouTubeVideo = url => {
  // Defensive: callers may pass migrated/scalar junk (number, object, …). Only a
  // non-empty string can carry a YouTube id; anything else yields no id rather
  // than throwing "url.match is not a function".
  if (typeof url !== 'string' || !url || url.length > MAX_YOUTUBE_LINK_LENGTH) {
    return null;
  }
  const match = url.match(YOUTUBE_VIDEO_PATTERN);
  if (!match) {
    return null;
  }
  return match[3]
    ? { id: match[4], host: match[3], path: '' }
    : { id: match[4], host: match[1], path: match[2] };
};

export const extractYouTubeID = url => {
  const video = matchYouTubeVideo(url);
  return video ? video.id : null;
};

// Ticket 24 — a CHANNEL url is the one shape `extractYouTubeID` cannot tell
// apart from "not YouTube at all": both fail its regex today, and the two
// need distinct messages (AC10). Checked ONLY once `extractYouTubeID` has
// already failed (see `isYouTubeChannelURL`'s caller) so a real video url
// can never be misread as a channel by a coincidental substring match.
//
// Ticket 24 fifth pass (review 2, L-F) — the same host boundary as the video
// pattern, so `evilyoutube.com/@someone` is "not a YouTube link", not a
// channel; and the same length cap, for the same reason (M-C).
const YOUTUBE_CHANNEL_PATTERN = /(?:^|[^a-z0-9_-])(?:[a-z0-9-]+\.)*youtube\.com\/(?:@[^\/\n\s]+|channel\/|c\/|user\/)/i;

export const isYouTubeChannelURL = url =>
  typeof url === 'string' &&
  url.length <= MAX_YOUTUBE_LINK_LENGTH &&
  YOUTUBE_CHANNEL_PATTERN.test(url);
